import {
  View,
  DataSet,
  Data,
  PropertySet,
  PropertyMap,
  PropertyDescriptor,
  PropertyDescriptorTypes,
  LookupPropertyDescriptor,
  Workbook,
  ViewConfig,
} from "../core/Workbook";
import {
  dictionaryValueMap,
  dictionaryKeyMap,
  dictionaryMap,
  PersistentKeyMapper,
} from "../utils/dictionaryUtils";
import { uuid } from "../utils/uuid";

/**
 * Functions for replicating set up of model objects with new uuids
 */
export function copyDataset(
  dataset: DataSet,
  propertyRemap: PersistentKeyMapper | undefined = undefined
): [DataSet, PersistentKeyMapper] {
  const propertyRemapper = !!propertyRemap
    ? propertyRemap
    : new PersistentKeyMapper((key) => uuid());
  propertyRemap = propertyRemap as PersistentKeyMapper;
  const newDatasetId = propertyRemapper.map(dataset.id);
  const data: Data = dictionaryValueMap(dataset.data, (row) =>
    dictionaryKeyMap(row, (key) => propertyRemapper.map(key))
  );
  function maybeColumnRemap(key: string): string {
    return propertyRemapper.map(key);
  }
  function maybeDatasetRemap(key: string) {
    return propertyRemapper.has(key) ? propertyRemapper.map(key) : key;
  }
  function descriptorRemap(descriptor: PropertyDescriptor): PropertyDescriptor {
    if (descriptor.type === PropertyDescriptorTypes.Lookup) {
      const lookupDescriptor = descriptor as LookupPropertyDescriptor;
      return {
        ...descriptor,
        ...{
          keyColumnInReferenceSheet: maybeColumnRemap(
            lookupDescriptor.keyColumnInReferenceSheet
          ),
          referenceColumnInReferenceSheet: maybeColumnRemap(
            lookupDescriptor.referenceColumnInReferenceSheet
          ),
          keyColumnInCurrentSheet: maybeColumnRemap(
            lookupDescriptor.keyColumnInCurrentSheet
          ),
          referenceSheet: maybeDatasetRemap(lookupDescriptor.referenceSheet),
        },
      };
    }
    return descriptor;
  }
  const descriptors: PropertyMap = dictionaryMap(
    dataset.properties.descriptors,
    (key) => propertyRemapper.map(key),
    (property) => descriptorRemap(property)
  );
  const keyToName: { [key: string]: string } = dictionaryKeyMap(
    dataset.properties.keyToName,
    (key) => propertyRemapper.map(key)
  );
  const nameToKey: { [key: string]: string } = dictionaryValueMap(
    dataset.properties.nameToKey,
    (key) => propertyRemapper.map(key)
  );
  const properties: PropertySet = {
    descriptors: descriptors,
    keyToName: keyToName,
    nameToKey: nameToKey,
  };
  return [
    {
      id: newDatasetId,
      data: data,
      properties: properties,
      name: dataset.name,
    },
    propertyRemapper,
  ];
}
class ViewReplicatorType {
  map: {
    [viewType: string]: (
      view: ViewConfig,
      propertyRemap: PersistentKeyMapper
    ) => ViewConfig;
  };
  constructor() {
    this.map = {};
  }
  addReplicator(
    viewType: string,
    replicator: (
      config: ViewConfig,
      propertyRemap: PersistentKeyMapper
    ) => ViewConfig
  ) {
    this.map[viewType] = replicator;
  }
  replicate(viewConfig: ViewConfig, propertyRemap: PersistentKeyMapper) {
    return this.map[viewConfig.type ?? "spreadsheet"](
      viewConfig,
      propertyRemap
    );
  }
}
export const ViewReplicator = new ViewReplicatorType();
export function copyView(
  newViewId: string,
  view: View,
  propertyRemap: PersistentKeyMapper
): View {
  return {
    ...view,
    ...{
      id: newViewId,
      config: ViewReplicator.replicate(view.config, propertyRemap),
    },
  };
}

export function cloneViewConfig(config: ViewConfig) {
  const DontMapProperties = new PersistentKeyMapper((key) => key);
  return ViewReplicator.replicate(config, DontMapProperties);
}

export function copyWorkbook(workbook: Workbook): Workbook {
  let globalGuidMap = new PersistentKeyMapper((key) => uuid());
  // Pre-populate with dataset id mappings, to enable lookup expression mapping
  Object.keys(workbook.data.datasets).forEach((datasetId) =>
    globalGuidMap.map(datasetId)
  );
  let newDatasets = dictionaryValueMap(workbook.data.datasets, (dataset) => {
    const [newDataset] = copyDataset(dataset, globalGuidMap);
    return newDataset;
  });
  let newViews = dictionaryValueMap(workbook.data.views, (view) => {
    const viewId = view.id;
    const newViewId = globalGuidMap.map(viewId);
    return copyView(newViewId, view, globalGuidMap);
  });
  const newTabs = workbook.data.viewOrder.map((value) =>
    globalGuidMap.map(value)
  );
  // Now remap keys with new ids
  newDatasets = dictionaryKeyMap(newDatasets, (id) => newDatasets[id].id);
  newViews = dictionaryKeyMap(newViews, (id) => newViews[id].id);
  return {
    operational: {
      selectedView: workbook.operational.selectedView,
      nextSheetNumber: workbook.operational.nextSheetNumber,
    },
    data: {
      datasets: newDatasets,
      views: newViews,
      viewOrder: newTabs,
    },
    name: workbook.name,
    id: uuid(),
    version: workbook.version,
  };
}
